// از دادهٔ خامِ `admin_users` تا چیزی که می‌شود در جدول نشانش داد.
//
// عمداً در core و نه در SQL: «چند روز نیامدن یعنی خوابیده» و «چند رویداد
// یعنی فعال» تصمیم‌های نمایشی‌اند و عوض کردنشان نباید مایگریشن بخواهد —
// همان تقسیمِ کاری که `journeyReport.ts` و `sourceTest.ts` دارند.
//
// و چون این‌جا هیچ وابستگیِ عارضه‌داری نیست، `npm test` با اعدادِ ساختگی
// می‌آزمایدش — بدونِ دیتابیس، بدونِ مرورگر.

/* ─────────────────────────── ورودی ─────────────────────────── */

/**
 * سه‌تاییِ استانداردِ داشبورد (`crawler_tri` در مایگریشن 0108).
 *
 * ⚠ در این سربرگ `run` یعنی **۲۴ ساعتِ اخیر** و نه «آخرین اجرای کرالر» —
 * این جدول‌ها را کاربر پر می‌کند نه کرالر. شکلش عمداً با `Counts` در
 * `data/opsRepo.ts` یکی است تا همان `<Two>`/`<Stat>` بخواندشان.
 */
export interface EventCounts {
  total: number;
  d10: number;
  run: number;
}

/** از کدام راه «داخلی» شد — و فقط `manual` با دکمه برمی‌گردد. */
export type InternalSource = 'email' | 'manual' | 'other' | null;

export interface AdminUser {
  id: string;
  username: string;
  display_name: string;
  /** ⚠ فقط به ادمین می‌رسد؛ چراییِ آمدنش سرِ مایگریشن 0149 است. */
  email: string | null;
  /**
   * بهترین شماره‌ای که از این حساب داریم — و شکلش **یکدست نیست**: اگر
   * سوپابیس یا کدِ پیامکیِ مصرف‌شده داشته باشیمش کاملِ E.164 است، وگرنه
   * فقط ۱۰ رقمِ آخر (`phone_key`). سه منبعش سرِ مایگریشن 0149 شمرده شده.
   *
   * پس این رشته را **نباید** جایی به‌عنوانِ شماره‌ی قابلِ تماس به کار برد؛
   * کارش شناختنِ آدم است، نه زنگ زدن به او.
   */
  phone: string | null;
  created_at: string;
  /** تازه‌ترین ردیفِ `app_events` این حساب. `null` یعنی هرگز دیده نشده. */
  last_seen: string | null;
  is_admin: boolean;
  internal: boolean;
  internal_source: InternalSource;
  internal_reason: string;
  events: EventCounts;
  sessions: number;
  signals: number;
  responses: number;
  friends: number;
}

export interface AdminUsersData {
  days: number;
  /** کلِ حساب‌ها، مستقل از جست‌وجو و سقف. */
  total: number;
  /** چند تا از آن‌ها داخلی‌اند. */
  internal: number;
  /** چند ردیف با جست‌وجوی فعلی خواند — پیش از سقف. */
  matched: number;
  /** چند ردیف واقعاً برگشت. */
  shown: number;
  q: string | null;
  users: AdminUser[];
}

/* ─────────────────────────── حکم ─────────────────────────── */

export type UserLevel = 'active' | 'light' | 'dormant' | 'never';

export const LEVEL_LABEL: Record<UserLevel, string> = {
  active: 'فعال',
  light: 'کم‌کار',
  dormant: 'خوابیده',
  never: 'فقط ثبت‌نام',
};

/**
 * ⚠ ترتیبِ این‌ها ترتیبِ ستون‌های خلاصه است و تصادفی نیست: از «بیشترین
 * ارزش» به «کمترین». صفحه‌ای که «فقط ثبت‌نام» را اول بنویسد، اولین عددی که
 * چشم می‌بیند بی‌ربط‌ترینشان است.
 */
export const LEVELS: UserLevel[] = ['active', 'light', 'dormant', 'never'];

/** از این تعداد روز که نیامده باشد، «خوابیده». */
export const DORMANT_DAYS = 30;

/** از این تعداد رویداد در ده روز به بالا، «فعال». */
export const ACTIVE_EVENTS_D10 = 20;

export interface UserRow extends AdminUser {
  level: UserLevel;
  /** چند روز از آخرین دیده‌شدن گذشته. `null` یعنی هرگز دیده نشده. */
  lastSeenDays: number | null;
  /** چند روز از ثبت‌نام گذشته. */
  ageDays: number;
  /** آیا این حساب با دکمه‌ی «تستی» برمی‌گردد — یعنی دستی علامت خورده. */
  canUnmark: boolean;
}

export interface UsersReport {
  rows: UserRow[];
  summary: {
    total: number;
    internal: number;
    /** `total - internal` — همان‌هایی که در آمارها شمرده می‌شوند. */
    real: number;
    matched: number;
    shown: number;
    /** جست‌وجو بیشتر از سقف خواند و چیزی نیامده — رابط باید بگوید. */
    capped: boolean;
    /**
     * ⚠ فقط روی حساب‌های **واقعی** شمرده می‌شود. شمارشی که حساب‌های تستی
     * را هم «فعال» بشمارد دقیقاً همان دروغی است که مایگریشن 0119 آمد
     * درستش کند.
     */
    levels: Record<UserLevel, number>;
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** چند روزِ کامل از `iso` تا `now`. `null` برای ورودیِ خالی یا نامعتبر. */
export function daysSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  // تاریخِ آینده (ساعتِ کج‌رفته‌ی یک دستگاه) صفر است و نه عددِ منفی.
  return Math.max(0, Math.floor((now - t) / DAY_MS));
}

/**
 * حکمِ یک ردیف.
 *
 * ⚠ ترتیبِ شاخه‌ها عمدی است: «فقط ثبت‌نام» قبل از «خوابیده» می‌آید، وگرنه
 * حسابی که هرگز اپ را باز نکرده «خوابیده» خوانده می‌شد — و آن دو دو خبرِ
 * کاملاً متفاوت‌اند. اولی یعنی ثبت‌نام گرفتیم و محصول را ندید، دومی یعنی
 * دید و برنگشت.
 */
export function userLevel(u: AdminUser, lastSeenDays: number | null): UserLevel {
  const touched = u.events.total > 0 || u.signals > 0 || u.responses > 0;
  if (!touched) return 'never';
  if (lastSeenDays === null || lastSeenDays > DORMANT_DAYS) return 'dormant';
  if (u.events.d10 >= ACTIVE_EVENTS_D10) return 'active';
  return 'light';
}

/* ─────────────────────────── ساخت ─────────────────────────── */

export interface UsersReportOptions {
  /** برای تست‌پذیری؛ پیش‌فرض همین حالا. */
  now?: number;
}

export function buildUsersReport(
  data: AdminUsersData,
  opts: UsersReportOptions = {},
): UsersReport {
  const now = opts.now ?? Date.now();
  const users = data?.users ?? [];

  const rows: UserRow[] = users.map((u) => {
    const lastSeenDays = daysSince(u.last_seen, now);
    return {
      ...u,
      lastSeenDays,
      ageDays: daysSince(u.created_at, now) ?? 0,
      level: userLevel(u, lastSeenDays),
      canUnmark: u.internal_source === 'manual',
    };
  });

  const levels: Record<UserLevel, number> = { active: 0, light: 0, dormant: 0, never: 0 };
  for (const r of rows) if (!r.internal) levels[r.level] += 1;

  const total = data?.total ?? 0;
  const internal = data?.internal ?? 0;
  const matched = data?.matched ?? rows.length;
  const shown = data?.shown ?? rows.length;

  return {
    rows,
    summary: {
      total,
      internal,
      real: Math.max(0, total - internal),
      matched,
      shown,
      capped: matched > shown,
      levels,
    },
  };
}
